import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useMemo, useState } from "react";
import { toast } from "sonner";

import { ProductShell, UpgradeNotice } from "@/components/app/product-shell";
import { ErrorBoundary } from "@/components/error-boundary";
import { useEntitlements } from "@/lib/billing/use-entitlements";
import {
  BAND_GUIDANCE,
  NEED_FACTORS,
  scoreNeed,
  suggestTemperature,
  type NeedAnswers,
} from "@/lib/seller/need-score";
import {
  analyzeTranscript,
  listSellerResponses,
  runAcquisitionAssistant,
  saveSellerResponse,
  type SellerAssistantResult,
  type TranscriptAnalysis,
} from "@/lib/seller/seller.functions";

export const Route = createFileRoute("/_authenticated/app/seller")({
  head: () => ({
    meta: [
      { title: "Seller intelligence — AROS FLOW" },
      {
        name: "description",
        content:
          "Turn a seller conversation into a Need Score, a motivation read and a clear next action, with every reason shown and nothing invented.",
      },
      { property: "og:title", content: "Seller intelligence — AROS FLOW" },
      {
        property: "og:description",
        content: "A guided seller questionnaire that scores need on evidence, not guesswork.",
      },
      { property: "og:type", content: "website" },
    ],
  }),
  component: SellerPage,
});

function SellerPage() {
  return (
    <ProductShell
      title="Seller"
      subtitle="Answer only what the seller actually told you. Anything you skip stays marked as missing rather than being guessed."
    >
      <ErrorBoundary label="Seller page crashed">
        <SellerBody />
      </ErrorBoundary>
    </ProductShell>
  );
}

type Tab = "questionnaire" | "transcript";

function SellerBody() {
  const ent = useEntitlements();
  const [tab, setTab] = useState<Tab>("questionnaire");

  if (!ent.loading && !ent.inPlan("need_score")) {
    return <UpgradeNotice feature="Seller intelligence" plan="Pro" />;
  }

  return (
    <div className="space-y-5">
      <div role="tablist" aria-label="Seller tools" className="flex gap-2">
        {(
          [
            ["questionnaire", "Questionnaire & Need Score"],
            ["transcript", "Transcript analysis"],
          ] as [Tab, string][]
        ).map(([id, label]) => (
          <button
            key={id}
            role="tab"
            type="button"
            aria-selected={tab === id}
            onClick={() => setTab(id)}
            className={`focus-key rounded-lg border px-3 py-2 text-xs font-semibold ${
              tab === id
                ? "border-[var(--gold)] bg-[var(--gold)]/10 text-gold"
                : "border-[var(--glass-border)] text-white/70 hover:bg-white/5"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === "questionnaire" ? <Questionnaire /> : <TranscriptPanel />}
    </div>
  );
}

// ---------------------------------------------------------------------------

function Questionnaire() {
  const qc = useQueryClient();
  const [answers, setAnswers] = useState<NeedAnswers>({});
  const [notes, setNotes] = useState("");
  const [address, setAddress] = useState("");
  const [sellerAsk, setSellerAsk] = useState("");
  const [assistant, setAssistant] = useState<SellerAssistantResult | null>(null);

  const result = useMemo(() => scoreNeed(answers), [answers]);
  const temperature = suggestTemperature(result.score, result.coverage);

  const runAssistant = useServerFn(runAcquisitionAssistant);
  const save = useServerFn(saveSellerResponse);

  const assistantMutation = useMutation({
    mutationFn: () =>
      runAssistant({
        data: {
          answers,
          notes,
          addressText: address,
          sellerAsk: Number(sellerAsk) || 0,
        },
      }),
    onSuccess: (r) => {
      if (!r.ok) {
        toast.error(r.blockedReason ?? "This is included on the Pro plan.");
        return;
      }
      setAssistant(r.result);
      toast.success("Assistant ready — these are recommendations, you decide.");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const saveMutation = useMutation({
    mutationFn: () =>
      save({
        data: {
          answers,
          notes,
          aiAnalysis: assistant
            ? (JSON.parse(JSON.stringify(assistant)) as Record<string, never>)
            : {},
        },
      }),
    onSuccess: () => {
      toast.success("Seller profile saved to your account");
      qc.invalidateQueries({ queryKey: ["seller-responses"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_340px]">
      <section className="space-y-3">
        <div className="grid gap-3 rounded-xl border border-[var(--glass-border)] bg-card p-4 sm:grid-cols-2">
          <div>
            <label htmlFor="s-address" className="text-[11px] text-muted-foreground">
              Property address (optional)
            </label>
            <input
              id="s-address"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              placeholder="123 Main St, City, ST"
              className="focus-key mt-1 w-full rounded-lg border border-[var(--glass-border)] bg-input px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label htmlFor="s-ask" className="text-[11px] text-muted-foreground">
              What they say they want
            </label>
            <input
              id="s-ask"
              inputMode="numeric"
              value={sellerAsk}
              onChange={(e) => setSellerAsk(e.target.value.replace(/[^0-9]/g, ""))}
              placeholder="Leave blank if they did not say"
              className="focus-key mt-1 w-full rounded-lg border border-[var(--glass-border)] bg-input px-3 py-2 text-sm"
            />
          </div>
        </div>

        {NEED_FACTORS.map((f) => {
          const value = answers[f.key];
          return (
            <fieldset
              key={f.key}
              className="rounded-xl border border-[var(--glass-border)] bg-card p-4"
            >
              <legend className="px-1 text-xs font-semibold text-gold">{f.label}</legend>
              <p className="text-xs text-muted-foreground">{f.question}</p>
              <div className="mt-3 grid gap-2 sm:grid-cols-2">
                {f.levels.map((level, i) => (
                  <button
                    key={level}
                    type="button"
                    aria-pressed={value === i}
                    onClick={() =>
                      setAnswers((s) => {
                        const next = { ...s };
                        if (next[f.key] === i) delete next[f.key];
                        else next[f.key] = i;
                        return next;
                      })
                    }
                    className={`focus-key rounded-lg border px-3 py-2 text-left text-[11px] ${
                      value === i
                        ? "border-[var(--gold)] bg-[var(--gold)]/10 text-gold"
                        : "border-[var(--glass-border)] text-white/75 hover:bg-white/5"
                    }`}
                  >
                    {level}
                  </button>
                ))}
              </div>
            </fieldset>
          );
        })}

        <div className="rounded-xl border border-[var(--glass-border)] bg-card p-4">
          <label htmlFor="seller-notes" className="text-xs font-semibold text-gold">
            Call notes
          </label>
          <textarea
            id="seller-notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={4}
            placeholder="What they said, in their words. Pain, timeline, objections."
            className="focus-key mt-2 w-full rounded-lg border border-[var(--glass-border)] bg-input p-3 text-sm"
          />
        </div>

        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => assistantMutation.mutate()}
            disabled={assistantMutation.isPending || result.coverage === 0}
            className="focus-key rounded-lg gradient-gold px-4 py-2 text-sm font-semibold text-black disabled:opacity-60"
          >
            {assistantMutation.isPending ? "Thinking…" : "Ask the acquisition assistant"}
          </button>
          <button
            type="button"
            onClick={() => saveMutation.mutate()}
            disabled={saveMutation.isPending || result.coverage === 0}
            className="focus-key rounded-lg border border-[var(--glass-border)] px-4 py-2 text-sm hover:bg-white/5 disabled:opacity-60"
          >
            Save to my account
          </button>
        </div>

        {assistant && <AssistantPanel result={assistant} />}
      </section>

      <aside className="space-y-3 lg:sticky lg:top-32 lg:self-start">
        <div className="rounded-xl border border-[var(--gold)]/40 bg-card p-5">
          <div className="text-[11px] uppercase tracking-widest text-muted-foreground">
            Need Score
          </div>
          <div className="mt-1 text-4xl font-semibold text-gold">{result.score}</div>
          <p className="mt-1 text-[11px] text-muted-foreground">
            A deterministic priority index built only from the answers you selected. Not a
            probability and not a prediction.
          </p>
          <div className="mt-3 rounded-lg border border-[var(--glass-border)] p-3">
            <div className="text-xs font-semibold">{result.band}</div>
            <p className="mt-1 text-[11px] text-muted-foreground">{BAND_GUIDANCE[result.band]}</p>
          </div>
          <div className="mt-3 flex items-center justify-between text-[11px] text-muted-foreground">
            <span>Evidence coverage: {result.coverage}%</span>
            <span className="uppercase tracking-wider">Suggested: {temperature}</span>
          </div>
        </div>

        <details className="rounded-xl border border-[var(--glass-border)] bg-card p-4">
          <summary className="focus-key cursor-pointer text-xs font-semibold text-gold">
            How this number was built
          </summary>
          <ul className="mt-2 space-y-1 text-[11px] text-muted-foreground">
            {result.contributions.map((c) => (
              <li key={c.key} className="flex justify-between gap-3">
                <span>
                  {c.label} — {c.levelLabel}
                </span>
                <span>
                  {c.points}/{c.maxPoints}
                </span>
              </li>
            ))}
          </ul>
        </details>

        <ReasonList title="Raising priority" items={result.drivers} tone="up" />
        <ReasonList title="Lowering priority" items={result.detractors} tone="down" />
        <ReasonList title="Still unknown" items={result.unknown} tone="missing" />
        <ReasonList
          title="Not connected in this build"
          items={result.placeholders}
          tone="missing"
        />
      </aside>
    </div>
  );
}

function AssistantPanel({ result }: { result: SellerAssistantResult }) {
  return (
    <div className="space-y-3 rounded-xl border border-[var(--gold)]/30 bg-card p-4">
      <div className="text-xs font-semibold text-gold">
        Acquisition assistant — recommendations, not instructions
      </div>
      <Field label="Motivation" body={result.motivation} />
      <Field label="Timeline" body={result.timeline} />
      <ListField label="Pain points" items={result.painPoints} />
      <div>
        <div className="text-[11px] font-semibold text-white/80">Objections and responses</div>
        <ul className="mt-1 space-y-2 text-[11px] text-muted-foreground">
          {result.objections.map((o) => (
            <li key={o.objection} className="rounded-lg border border-[var(--glass-border)] p-2">
              <div className="text-white/80">“{o.objection}”</div>
              <div className="mt-1">{o.response}</div>
            </li>
          ))}
        </ul>
      </div>
      <ListField label="Questions to ask next" items={result.nextQuestions} />
      <Field label="If they counter" body={result.counterGuidance} />
      <Field label="Walk-away point" body={result.walkAwayGuidance} />
      <Field label="Next best action" body={result.nextBestAction} />
      <Field label="Why the assistant said this" body={result.reasoning} />
    </div>
  );
}

function Field({ label, body }: { label: string; body: string }) {
  if (!body) return null;
  return (
    <div>
      <div className="text-[11px] font-semibold text-white/80">{label}</div>
      <p className="text-[11px] text-muted-foreground">{body}</p>
    </div>
  );
}

function ListField({ label, items }: { label: string; items: string[] }) {
  if (!items?.length) return null;
  return (
    <div>
      <div className="text-[11px] font-semibold text-white/80">{label}</div>
      <ul className="mt-1 space-y-1 text-[11px] text-muted-foreground">
        {items.map((i) => (
          <li key={i}>· {i}</li>
        ))}
      </ul>
    </div>
  );
}

// ---------------------------------------------------------------------------

function TranscriptPanel() {
  const qc = useQueryClient();
  const analyze = useServerFn(analyzeTranscript);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [analysis, setAnalysis] = useState<TranscriptAnalysis | null>(null);

  const run = useMutation({
    mutationFn: () => analyze({ data: { title: title || "Call transcript", body } }),
    onSuccess: (r) => {
      if (!r.ok) {
        toast.error(r.blockedReason ?? "This is included on the Pro plan.");
        return;
      }
      setAnalysis(r.analysis);
      qc.invalidateQueries({ queryKey: ["transcripts"] });
      toast.success("Transcript structured");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_340px]">
      <section className="space-y-3">
        <div className="rounded-xl border border-[var(--glass-border)] bg-card p-4">
          <label htmlFor="t-title" className="text-[11px] text-muted-foreground">
            Label
          </label>
          <input
            id="t-title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Seller name or address"
            className="focus-key mt-1 w-full rounded-lg border border-[var(--glass-border)] bg-input px-3 py-2 text-sm"
          />
          <label htmlFor="t-body" className="mt-3 block text-[11px] text-muted-foreground">
            Paste the call transcript or your raw notes
          </label>
          <textarea
            id="t-body"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={14}
            placeholder="Paste the conversation here. Nothing is inferred that is not in the text."
            className="focus-key mt-1 w-full rounded-lg border border-[var(--glass-border)] bg-input p-3 text-sm"
          />
          <button
            type="button"
            onClick={() => run.mutate()}
            disabled={run.isPending || body.trim().length < 40}
            className="focus-key mt-3 rounded-lg gradient-gold px-4 py-2 text-sm font-semibold text-black disabled:opacity-60"
          >
            {run.isPending ? "Reading…" : "Structure this call"}
          </button>
        </div>
      </section>

      <aside className="space-y-3">
        {analysis ? (
          <>
            <div className="rounded-xl border border-[var(--gold)]/30 bg-card p-4">
              <div className="text-xs font-semibold text-gold">Summary</div>
              <p className="mt-1 text-[11px] text-muted-foreground">{analysis.summary}</p>
            </div>
            <ListCard title="Motivation signals" items={analysis.motivationSignals} />
            <ListCard title="Timeline signals" items={analysis.timelineSignals} />
            <ListCard title="Objections raised" items={analysis.objections} />
            <ListCard title="Numbers they stated" items={analysis.statedNumbers} />
            <ListCard title="Recommended follow-ups" items={analysis.recommendedFollowUps} />
            {analysis.suggestedAnswers.length > 0 && (
              <div className="rounded-xl border border-[var(--glass-border)] bg-card p-4">
                <div className="text-xs font-semibold text-gold">
                  Suggested questionnaire answers
                </div>
                <ul className="mt-2 space-y-1 text-[11px] text-muted-foreground">
                  {analysis.suggestedAnswers.map((s) => (
                    <li key={s.factor}>
                      <span className="text-white/80">{s.factor}</span> → level {s.level}: “
                      {s.evidence}”
                    </li>
                  ))}
                </ul>
                <p className="mt-2 text-[10px] text-muted-foreground">
                  Suggestions only. Set them yourself on the questionnaire tab so the score stays
                  yours.
                </p>
              </div>
            )}
          </>
        ) : (
          <p className="rounded-xl border border-[var(--glass-border)] bg-card p-5 text-[11px] text-muted-foreground">
            Paste a call and ArosFlow will pull out motivation, timeline, objections and the numbers
            that were actually said — and nothing that was not.
          </p>
        )}
      </aside>
    </div>
  );
}

function ListCard({ title, items }: { title: string; items: string[] }) {
  if (!items?.length) return null;
  return (
    <div className="rounded-xl border border-[var(--glass-border)] bg-card p-4">
      <div className="text-xs font-semibold text-gold">{title}</div>
      <ul className="mt-2 space-y-1 text-[11px] text-muted-foreground">
        {items.map((i) => (
          <li key={i}>· {i}</li>
        ))}
      </ul>
    </div>
  );
}

function ReasonList({
  title,
  items,
  tone,
}: {
  title: string;
  items: string[];
  tone: "up" | "down" | "missing";
}) {
  if (!items.length) return null;
  const color =
    tone === "up" ? "text-success" : tone === "down" ? "text-warning" : "text-muted-foreground";
  return (
    <div className="rounded-xl border border-[var(--glass-border)] bg-card p-4">
      <div className={`text-xs font-semibold ${color}`}>{title}</div>
      <ul className="mt-2 space-y-1 text-[11px] text-muted-foreground">
        {items.map((i) => (
          <li key={i}>· {i}</li>
        ))}
      </ul>
    </div>
  );
}

export { listSellerResponses };
